import { useEffect, useMemo, useState } from 'react';
import { atom, useAtomValue, useAtom } from 'jotai';
import {
  Button,
  ComposedModal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from '@carbon/react';
import { WarningAltFilled, Location } from '@carbon/icons-react';
import { tcOverlappingUnitsAtom } from 'pages/type-curves/type-curve-filters/atoms';
import { activeTcWorkspaceAtom } from '../atoms';
import styles from './overlap-onboarding-modal.module.scss';

// Tracks which workspace the overlap modal has already been shown for.
// Lives in the Jotai store so it survives component unmount/remount cycles
// (e.g. when TypeCurveMap early-returns a loading spinner).
const overlapModalShownForAtom = atom<number | null>(null);
export const OverlapOnboardingModal = () => {
  const workspace = useAtomValue(activeTcWorkspaceAtom);
  const overlapping = useAtomValue(tcOverlappingUnitsAtom);
  const [open, setOpen] = useState(false);

  const workspaceId = workspace?.id ?? null;
  const overlapCount = overlapping.length;

  // Distinct units involved in any overlap + unique unordered overlap pairs,
  // matching the mock copy ("6 units … (4 pairs)").
  const { unitCount, pairCount } = useMemo(() => {
    const units = new Set<number>();
    const pairs = new Set<string>();
    for (const o of overlapping) {
      units.add(o.id);
      for (const p of o.overlappingUnits ?? []) {
        units.add(p.id);
        pairs.add(`${Math.min(o.id, p.id)}-${Math.max(o.id, p.id)}`);
      }
    }
    return { unitCount: units.size, pairCount: pairs.size };
  }, [overlapping]);

  // Open once per workspace. The atom persists in the Jotai store across
  // unmount/remount cycles, so transient loading states in the parent
  // (TypeCurveMap) no longer reset the guard.
  const [shownForWorkspace, setShownForWorkspace] = useAtom(
    overlapModalShownForAtom
  );
  useEffect(() => {
    if (workspaceId === null) return;
    if (overlapCount > 0 && shownForWorkspace !== workspaceId) {
      setShownForWorkspace(workspaceId);
      setOpen(true);
    }
  }, [workspaceId, overlapCount, shownForWorkspace, setShownForWorkspace]);

  const dismiss = () => setOpen(false);

  if (workspaceId === null) return null;

  return (
    <ComposedModal open={open} onClose={dismiss} size="sm">
      <ModalHeader
        label="Heads up — inventory work blocked"
        title="Resolve overlapping units to manage inventory"
      />
      <ModalBody>
        <div className={styles.body}>
          <div className={styles.callout}>
            <span className={styles.calloutIcon} aria-hidden="true">
              <WarningAltFilled />
            </span>
            <div className={styles.calloutCopy}>
              <p className={styles.calloutLead}>
                This Type Curve has{' '}
                <strong>
                  {unitCount} {unitCount === 1 ? 'unit' : 'units'}
                </strong>{' '}
                with major geometry overlaps (
                <strong>
                  {pairCount} {pairCount === 1 ? 'pair' : 'pairs'}
                </strong>
                ).
              </p>
              <p className={styles.calloutBody}>
                Overlaps only block the <strong>inventory</strong> side of the
                workspace — Generate LTD geometries, per-unit verdicts, and the
                Verify Inventory task all stay locked until every overlap is
                resolved. You can still browse charts, leave comments, and mark
                the type curve Correct or Incorrect on other grounds, but if you
                need an inventory pass, start here.
              </p>
            </div>
          </div>

          <ol className={styles.steps}>
            <li>
              <span className={styles.stepNum}>1</span>
              <span>
                Click any <strong className={styles.swatch}>red region</strong>{' '}
                on the map — those are the overlapping areas. The unit you click
                becomes the selected unit.
              </span>
            </li>
            <li>
              <span className={styles.stepNum}>2</span>
              <span>
                A <strong>side panel</strong> opens listing every unit that
                conflicts with your selection. Pick which one to{' '}
                <strong>Keep</strong> — the others are dropped from the
                workspace.
              </span>
            </li>
            <li>
              <span className={styles.stepNum}>3</span>
              <span>
                Repeat for every overlap pair. The pulse stops on the map once
                the last pair is cleared. The{' '}
                <strong>Resolve unit overlaps</strong> task takes a verdict
                whenever you are ready, with pairs still open. Promoting needs
                that verdict, correct or incorrect.
              </span>
            </li>
          </ol>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button kind="secondary" onClick={dismiss}>
          Skip for now
        </Button>
        <Button kind="primary" renderIcon={Location} onClick={dismiss}>
          Show me on the map
        </Button>
      </ModalFooter>
    </ComposedModal>
  );
};

OverlapOnboardingModal.displayName = 'OverlapOnboardingModal';
